import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import {
  ArrowLeft,
  Loader2,
  Plus,
  Send,
  MessageSquare,
  AlertCircle,
} from 'lucide-react';
import {
  useListEmployeeTicketsQuery,
  useGetEmployeeTicketQuery,
  useOpenEmployeeTicketMutation,
  useReplyEmployeeTicketMutation,
} from '@/store/api/apiSlice';
import type { SupportTicket, SupportMessage, TicketCategory } from '@/types';

const STATUS_LABEL: Record<string, string> = {
  open: 'Open',
  in_progress: 'In Progress',
  waiting_on_employee: 'Waiting on You',
  resolved: 'Resolved',
  closed: 'Closed',
};

const STATUS_CLASS: Record<string, string> = {
  open: 'bg-amber-100 text-amber-700',
  in_progress: 'bg-blue-100 text-blue-700',
  waiting_on_employee: 'bg-purple-100 text-purple-700',
  resolved: 'bg-green-100 text-green-700',
  closed: 'bg-gray-200 text-gray-700',
};

const CATEGORY_OPTIONS: { value: TicketCategory | string; label: string }[] = [
  { value: 'investigation', label: 'Investigation' },
  { value: 'technical', label: 'Technical' },
  { value: 'billing', label: 'Billing' },
  { value: 'account', label: 'Account' },
  { value: 'general', label: 'General' },
  { value: 'other', label: 'Other' },
];

function formatWhen(value?: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function readErrorMessage(err: any, fallback = 'Something went wrong'): string {
  return err?.data?.message ?? err?.message ?? fallback;
}

export default function EmployeeSupport() {
  const { supportCode: paramCode } = useParams<{ supportCode: string }>();
  const [searchParams] = useSearchParams();
  const codeFromUrl = paramCode || searchParams.get('code') || '';

  const [supportCode, setSupportCode] = useState(codeFromUrl);
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
  const [showNewTicketForm, setShowNewTicketForm] = useState(false);

  useEffect(() => {
    setSupportCode(codeFromUrl);
  }, [codeFromUrl]);

  if (!supportCode) {
    return <SupportCodePrompt onSubmit={setSupportCode} />;
  }

  return (
    <div className="min-h-screen bg-[#f5f5f5]">
      <header className="bg-white border-b border-[#e0e0e0]">
        <div className="mx-auto max-w-[1024px] px-4 md:px-6 py-4 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs text-[#717680]">Support code</p>
            <p className="font-mono text-sm md:text-base font-bold text-[#151515] tracking-[0.04em] truncate">
              {supportCode}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowNewTicketForm(true)}
            className="bg-[#FF6B35] hover:bg-[#FF5722] text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            New ticket
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-[1024px] px-4 md:px-6 py-6">
        {selectedTicketId ? (
          <TicketThreadView
            supportCode={supportCode}
            ticketId={selectedTicketId}
            onBack={() => setSelectedTicketId(null)}
          />
        ) : (
          <TicketListView
            supportCode={supportCode}
            onSelect={(id) => setSelectedTicketId(id)}
          />
        )}
      </main>

      {showNewTicketForm && (
        <NewTicketModal
          supportCode={supportCode}
          onClose={() => setShowNewTicketForm(false)}
          onCreated={(t) => {
            setShowNewTicketForm(false);
            setSelectedTicketId(t.id);
          }}
        />
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Sub-views
// ──────────────────────────────────────────────────────────────────────────────

function SupportCodePrompt({ onSubmit }: { onSubmit: (code: string) => void }) {
  const [code, setCode] = useState('');
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f5f5f5] px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm p-6 md:p-8">
        <div className="w-12 h-12 rounded-full bg-[#FF6B35]/10 flex items-center justify-center mb-4">
          <MessageSquare className="w-5 h-5 text-[#FF6B35]" />
        </div>
        <h1 className="text-xl font-bold text-[#151515] mb-1">YourChecka support</h1>
        <p className="text-sm text-[#4e4d4d] mb-6">
          Enter the support code we emailed you after you submitted your consent form.
        </p>
        <label className="block text-sm font-medium text-[#1b1b1b] mb-1">Support code</label>
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value.trim())}
          placeholder="PC-XXXX-XXXX"
          className="w-full bg-white border border-[#e0e0e0] rounded-lg h-12 px-3 text-sm font-mono tracking-wider outline-none focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/15"
        />
        <button
          type="button"
          disabled={!code}
          onClick={() => onSubmit(code)}
          className="mt-4 w-full bg-[#151515] hover:bg-[#2a2a2a] disabled:opacity-50 text-white py-2.5 rounded-lg text-sm font-medium"
        >
          Continue
        </button>
      </div>
    </div>
  );
}

function TicketListView({
  supportCode,
  onSelect,
}: {
  supportCode: string;
  onSelect: (id: string) => void;
}) {
  const { data: tickets, isLoading, error } = useListEmployeeTicketsQuery({ supportCode });

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-6 h-6 text-[#FF6B35] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white border border-red-200 rounded-2xl p-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-red-700">Couldn't load your tickets</p>
            <p className="text-xs text-red-600 mt-1">
              {readErrorMessage(error, 'Please check your support code and try again.')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!tickets || tickets.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-[#e0e0e0] p-10 text-center">
        <div className="w-12 h-12 rounded-full bg-[#f5f5f5] flex items-center justify-center mx-auto mb-3">
          <MessageSquare className="w-5 h-5 text-[#4e4d4d]" />
        </div>
        <p className="text-base font-semibold text-[#1b1b1b]">No tickets yet</p>
        <p className="text-sm text-[#4e4d4d] mt-1">
          Click "New ticket" if you need to message the YourChecka team about your verification.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-[#e0e0e0] overflow-hidden">
      <ul>
        {tickets.map((t) => (
          <li key={t.id}>
            <button
              type="button"
              onClick={() => onSelect(t.id)}
              className="w-full text-left flex items-start gap-3 px-4 md:px-5 py-4 border-b border-[#f0f0f0] last:border-b-0 hover:bg-[#fafafa] transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-semibold text-[#151515] truncate">{t.subject}</span>
                  <span
                    className={`shrink-0 inline-block text-[10px] font-medium px-2 py-0.5 rounded-md ${
                      STATUS_CLASS[t.status] ?? 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {STATUS_LABEL[t.status] ?? t.status}
                  </span>
                </div>
                <p className="text-xs text-[#4e4d4d] truncate">{t.message}</p>
                <p className="text-[11px] text-[#717680] mt-1">
                  Updated {formatWhen(t.lastMessageAt || t.updatedAt)}
                </p>
              </div>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TicketThreadView({
  supportCode,
  ticketId,
  onBack,
}: {
  supportCode: string;
  ticketId: string;
  onBack: () => void;
}) {
  const { data: ticket, isLoading, error } = useGetEmployeeTicketQuery({ id: ticketId, supportCode });
  const [reply, { isLoading: isReplying }] = useReplyEmployeeTicketMutation();
  const [body, setBody] = useState('');
  const [replyError, setReplyError] = useState('');

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-6 h-6 text-[#FF6B35] animate-spin" />
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className="bg-white border border-red-200 rounded-2xl p-6">
        <button type="button" onClick={onBack} className="flex items-center gap-1 text-xs text-[#4e4d4d] mb-3 hover:underline">
          <ArrowLeft className="w-3.5 h-3.5" />
          Back
        </button>
        <p className="text-sm text-red-700">{readErrorMessage(error, "We couldn't load this ticket.")}</p>
      </div>
    );
  }

  const messages: SupportMessage[] = ticket.messages ?? [];
  const isLocked = ticket.status === 'resolved' || ticket.status === 'closed';

  const handleReply = async () => {
    const trimmed = body.trim();
    if (!trimmed) {
      setReplyError('Type a message before sending.');
      return;
    }
    setReplyError('');
    try {
      await reply({ id: ticket.id, data: { supportCode, body: trimmed } }).unwrap();
      setBody('');
    } catch (err: any) {
      setReplyError(readErrorMessage(err, 'Failed to send your reply.'));
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Ticket header */}
      <div className="bg-white border border-[#e0e0e0] rounded-2xl px-5 py-4">
        <button type="button" onClick={onBack} className="flex items-center gap-1 text-xs text-[#4e4d4d] mb-2 hover:underline">
          <ArrowLeft className="w-3.5 h-3.5" />
          All tickets
        </button>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-base md:text-lg font-bold text-[#151515]">{ticket.subject}</h2>
          <span
            className={`inline-block text-[11px] font-medium px-2.5 py-1 rounded-md ${
              STATUS_CLASS[ticket.status] ?? 'bg-gray-100 text-gray-700'
            }`}
          >
            {STATUS_LABEL[ticket.status] ?? ticket.status}
          </span>
        </div>
        <p className="text-xs text-[#717680] mt-1">
          Opened {formatWhen(ticket.createdAt)}
          {ticket.investigationId ? ` · Investigation ${ticket.investigationId.slice(0, 8)}` : ''}
        </p>
      </div>

      {/* Thread */}
      <div className="bg-white border border-[#e0e0e0] rounded-2xl p-4 md:p-5 flex flex-col gap-3">
        {messages.length === 0 ? (
          <p className="text-sm text-[#717680]">No messages on this ticket yet.</p>
        ) : (
          messages.map((m) => <MessageBubble key={m.id} message={m} />)
        )}
      </div>

      {/* Reply box */}
      {isLocked ? (
        <div className="bg-[#fafafa] border border-[#e9eaeb] rounded-2xl px-5 py-4 text-sm text-[#4e4d4d]">
          This ticket is {(STATUS_LABEL[ticket.status] ?? ticket.status).toLowerCase()}. Open a new ticket if you have a follow-up.
        </div>
      ) : (
        <div className="bg-white border border-[#e0e0e0] rounded-2xl p-4 md:p-5">
          <label className="block text-sm font-medium text-[#1b1b1b] mb-2">Reply</label>
          <textarea
            value={body}
            onChange={(e) => {
              setBody(e.target.value);
              setReplyError('');
            }}
            rows={4}
            placeholder="Type your message..."
            className="w-full bg-white border border-[#e0e0e0] rounded-lg px-3 py-2.5 text-sm text-[#1b1b1b] outline-none focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/15 resize-y"
          />
          {replyError && <p className="mt-2 text-xs text-red-600">{replyError}</p>}
          <div className="flex justify-end mt-3">
            <button
              type="button"
              onClick={handleReply}
              disabled={isReplying}
              className="bg-[#151515] hover:bg-[#2a2a2a] disabled:opacity-60 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2"
            >
              {isReplying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Send reply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function MessageBubble({ message }: { message: SupportMessage }) {
  const isAdmin = message.senderType === 'admin';
  return (
    <div className={`flex ${isAdmin ? 'justify-start' : 'justify-end'}`}>
      <div
        className={`max-w-[85%] rounded-2xl px-4 py-3 ${
          isAdmin ? 'bg-[#f5f5f5] text-[#151515]' : 'bg-[#FF6B35] text-white'
        }`}
      >
        <div className="flex items-center gap-2 mb-1">
          <span className={`text-[11px] font-semibold ${isAdmin ? 'text-[#1b1b1b]' : 'text-white/90'}`}>
            {message.senderName || (isAdmin ? 'Support' : 'You')}
          </span>
          <span className={`text-[10px] ${isAdmin ? 'text-[#717680]' : 'text-white/80'}`}>
            {formatWhen(message.createdAt)}
          </span>
        </div>
        <p className="text-sm whitespace-pre-wrap leading-relaxed">{message.body}</p>
      </div>
    </div>
  );
}

function NewTicketModal({
  supportCode,
  onClose,
  onCreated,
}: {
  supportCode: string;
  onClose: () => void;
  onCreated: (ticket: SupportTicket) => void;
}) {
  const [open, { isLoading }] = useOpenEmployeeTicketMutation();
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [category, setCategory] = useState<string>('investigation');
  const [error, setError] = useState('');

  const submit = async () => {
    setError('');
    if (!subject.trim()) {
      setError('Subject is required');
      return;
    }
    if (!message.trim()) {
      setError('Message is required');
      return;
    }
    try {
      const result = await open({
        supportCode,
        subject: subject.trim(),
        message: message.trim(),
        category,
      }).unwrap();
      onCreated(result);
    } catch (err: any) {
      setError(readErrorMessage(err, 'Failed to open ticket. Please try again.'));
    }
  };

  const categoryOptions = useMemo(() => CATEGORY_OPTIONS, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl">
        <div className="p-5">
          <h3 className="text-base font-bold text-[#151515] mb-1">New support ticket</h3>
          <p className="text-xs text-[#4e4d4d] mb-4">
            Tell us what's going on and we'll get back to you over email and on this thread.
          </p>

          <label className="block text-sm font-medium text-[#1b1b1b] mb-1">Subject</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => {
              setSubject(e.target.value);
              setError('');
            }}
            placeholder="e.g. I uploaded the wrong government ID"
            className="w-full mb-3 bg-white border border-[#e0e0e0] rounded-lg h-11 px-3 text-sm outline-none focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/15"
          />

          <label className="block text-sm font-medium text-[#1b1b1b] mb-1">Category</label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full mb-3 bg-white border border-[#e0e0e0] rounded-lg h-11 px-3 text-sm outline-none focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/15"
          >
            {categoryOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          <label className="block text-sm font-medium text-[#1b1b1b] mb-1">Message</label>
          <textarea
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
              setError('');
            }}
            rows={5}
            placeholder="Describe what's happening..."
            className="w-full bg-white border border-[#e0e0e0] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/15 resize-y"
          />

          {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
        </div>
        <div className="flex gap-3 p-5 pt-0">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 py-2.5 border border-[#e0e0e0] rounded-lg text-sm font-medium text-[#1b1b1b] hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={isLoading}
            className="flex-1 bg-[#FF6B35] hover:bg-[#FF5722] text-white py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Open ticket
          </button>
        </div>
      </div>
    </div>
  );
}
